import { Button, Message, Modal, Stack } from '@kvdbil/components';
import React, { useState } from 'react';
import Select, {
  components,
  GroupBase,
  OptionProps,
  SingleValueProps
} from 'react-select';
import { DOMAIN_PATH_LOCALES } from '~/config/constants';
import { useDispatch, useSelector } from 'react-redux';
import { localizationSelector } from '~/App/shared/selectors/localization';
import { useTranslation } from '~/Locale';
import { generatePath } from 'react-router';
import { useLocation } from 'react-router-dom';
import { appStartLoading } from '~/App/shared/actions/app';
import styled from 'styled-components';
import { trimTrailingSlash } from '~/App/shared/localization/helpers';
import { fetchLocalizedRoute } from '~/helpers/orchestration/app';
import {
  getLanguageSelectStyles,
  LanguageSelectVariant
} from '~/App/shared/localization/components/selectStyles';
import {
  buildCountryGroup,
  Country,
  countries
} from '~/App/shared/localization/components/constants';
import { useLanguageSelection } from '~/App/shared/localization/hooks/useLanguageSelection';
import { useLastKeyboardInteraction } from '../../hooks/useLastKeyboardInteraction';
import { useIsDevice } from '../../hooks/useIsDevice';

const StyledMessage = styled(Message)`
  margin-bottom: 1.5rem;
`;

const Option = (props: OptionProps<Country, false, GroupBase<Country>>) => {
  return (
    <components.Option {...props}>
      <span>{props.data.icon}</span>
      <span>{props.data.label}</span>
    </components.Option>
  );
};

type Props = {
  variant?: LanguageSelectVariant;
};

function LanguageSelect({ variant = 'desktopHeader' }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { search, pathname } = useLocation();
  const {
    currentLocale,
    currentRoute: { originalPathname, params }
  } = useSelector(localizationSelector);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { focusByKeyboard, onFocus, onBlur } = useLastKeyboardInteraction();
  const { isMobile } = useIsDevice();
  const isDesktopHeader = variant === 'desktopHeader';

  // Options are grouped by where the locale lives (DOMAIN_PATH_LOCALES): the
  // kvd.se locales under "Within Sweden" and the kvdcars.com locales under
  // "Export". The group headers also disambiguate the two "English" entries
  // (domestic en-SE vs export en), which otherwise share a label.
  const groupedOptions: GroupBase<Country>[] = [
    buildCountryGroup(t('Within Sweden'), DOMAIN_PATH_LOCALES.sv),
    buildCountryGroup(t('Outside Sweden'), DOMAIN_PATH_LOCALES.en)
  ];

  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>(
    () => {
      return countries.find(country => country.value === currentLocale);
    }
  );

  const { getRedirectUrl } = useLanguageSelection();

  const handleChange = async (selectedCountry: Country) => {
    if (selectedCountry.value === currentLocale) return;

    setSelectedCountry(
      countries.find(country => country.value === selectedCountry.value)
    );

    if (!originalPathname || !currentLocale) {
      setIsModalOpen(true);
      return;
    }

    if (originalPathname === '/') {
      dispatch(appStartLoading());
      window.location.href = getRedirectUrl(selectedCountry.value, search);
      return;
    }

    try {
      const translatedPath = await fetchLocalizedRoute({
        currentLocale,
        requestedLocale: selectedCountry.value,
        originalRoute: originalPathname,
        currentPathname: pathname,
        params
      });

      const generatedPath = trimTrailingSlash(
        generatePath(translatedPath, params)
      );
      const redirectUrl = getRedirectUrl(
        selectedCountry.value,
        `${generatedPath}${search}`
      );

      dispatch(appStartLoading());

      window.location.href = redirectUrl;
    } catch (e) {
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setSelectedCountry(
      countries.find(country => country.value === currentLocale)
    );
    setIsModalOpen(false);
  };

  const SingleValue = ({
    children,
    ...props
  }: SingleValueProps<Country, false, GroupBase<Country>>) => (
    <components.SingleValue {...props}>
      <span>{selectedCountry?.icon}</span>
      {!isDesktopHeader ? null : <span>{children}</span>}
    </components.SingleValue>
  );

  return (
    <>
      <Select<Country, false, GroupBase<Country>>
        instanceId={`language-select-${variant}`}
        value={selectedCountry}
        options={groupedOptions}
        onChange={handleChange}
        components={{
          IndicatorSeparator: () => null,
          Option,
          SingleValue
        }}
        styles={{
          ...getLanguageSelectStyles(variant, focusByKeyboard, isMobile)
        }}
        isSearchable={false}
        aria-label={t('Choose language')}
        onFocus={onFocus}
        onBlur={onBlur}
        menuPosition={isDesktopHeader ? 'absolute' : 'fixed'}
      />
      <Modal isOpen={isModalOpen} onClose={handleModalClose} withPortal>
        <StyledMessage withIcon type="warning">
          {t('The current page cannot be switched to the selected language.')}
        </StyledMessage>

        {selectedCountry && (
          <Stack align="center">
            <Button
              color="info"
              variant="outline"
              as="span"
              prefixIcon={selectedCountry?.icon}
              style={{ width: 'fit-content' }}
              onClick={() => {
                if (selectedCountry?.value) {
                  dispatch(appStartLoading());
                  window.location.href = getRedirectUrl(
                    selectedCountry?.value,
                    '/'
                  );
                }
              }}
            >
              {t('Go to startpage')}
            </Button>
          </Stack>
        )}
      </Modal>
    </>
  );
}

export default LanguageSelect;
